// lista pozycji listy zakupowej
// @ts-nocheck

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Checkbox as MuiCheckbox } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';

import {
  useGetShoppingListPositions,
  useDeleteShoppingListPosition,
  usePutShoppingListPosition,
  usePostShoppingListPositionQuantityDecrement,
  usePostShoppingListPositionQuantityIncrement,
  usePostShoppingListExport
} from 'api';
import { IShoppingListPositionListItem, IShoppingListPositionsRequest, IUnit } from 'api/types';
import { Button, Checkbox, Counter, Link, Modal, SearchInput } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';
import Table, { IColumn } from 'components/controls/Table';
import { AddPositionsToCartSelect, AddToCartButton } from 'components/containers';
import { hasSpecialPriceLabel } from 'utils/productHelpers';

import { FileExportIcon, TrashIcon, ArrowDropdown } from 'assets/icons';
import styles from 'theme/pages/ShoppingLists/components/ShoppingListPositions/ShoppingListPositions.module.scss';
import stylesButton from "theme/components/containers/ShoppingListButton/ShoppingListButton.module.scss";

// typ danych wejściowych
interface IProps {
  isBlocked: boolean;
  shoppingListId: number;
  refetchShoppingLists: () => void;
}

type IExportFileType = 'CSV' | 'XLS';

// Komponent komórki z Counter i przyciskiem dodaj do koszyka (wzór: ProductCard)
const AddToCartCell: FC<{
  item: IShoppingListPositionListItem;
  disabled: boolean;
}> = ({ item, disabled }) => {
  const [quantity, setQuantity] = useState(item.quantity);

  return (
    <div className={stylesButton.addToCartCellWrapper}>
      <div className={stylesButton.addToCartRow}>
        <Counter
          value={quantity}
          onChange={(value) => setQuantity(Math.max(1, value))}
          onIncrease={() => setQuantity((q) => q + 1)}
          onDecrease={() => setQuantity((q) => Math.max(1, q - 1))}
          disabled={disabled}
          className={stylesButton.addToCartCounter}
        />
        <AddToCartButton
          productId={item.product_id}
          unitId={item.unit_id}
          quantity={quantity}
          disabled={disabled}
        />
      </div>
      {item.unit_name && (
        <span className={stylesButton.addToCartUnit}>{item.unit_name}</span>
      )}
    </div>
  );
};

const ShoppingListPositions: FC<IProps> = ({
  shoppingListId,
  refetchShoppingLists,
  isBlocked,
}) => {
  const { t } = useTranslation();

  // parametry zapytania do API o pozycje listy zakupowej
  const [queryParams, setQueryParams] = useState<IShoppingListPositionsRequest>(
    {
      page: 1,
      limit: 9999,
      search_keyword: "",
    }
  );

  // fraza wyszukiwania produktów w liście zakupowej
  const [searchQuery, setSearchQuery] = useState("");

  // aktualizacja parametrów zapytania po zatwierdzeniu frazy
  useEffect(() => {
    setQueryParams((prevState) => ({
      ...prevState,
      search_keyword: searchQuery,
      page: 1,
    }));
  }, [searchQuery]);

  // zaznaczone pozycje
  const [checkedItemIds, setCheckedItemIds] = useState<number[]>([]);

  // pozycja wybrana do usunięcia
  const [itemToDelete, setItemToDelete] =
    useState<IShoppingListPositionListItem | null>(null);

  // IDlki aktualizujących się pozycji
  const [updatingPositionIds, setUpdatingPositionIds] = useState<number[]>([]);

  // wybrany rodzaj exportu
  const [exportFileType, setExportFileType] = useState<IExportFileType>("XLS");

  // pozycje listy zakupowej
  const { data: shoppingListPositions, refetch: refetchShoppingListPositions } =
    useGetShoppingListPositions(shoppingListId, queryParams);

  // skasowanie pozycji z listy zakupowej
  const { mutate: deleteShoppingListPosition, isLoading: isRemoving } =
    useDeleteShoppingListPosition(shoppingListId, itemToDelete?.id || 0, {
      onSuccess: () => {
        setItemToDelete(null);
        refetchShoppingListPositions();
        refetchShoppingLists();
      },
    });

  // skasowanie pozycji z listy zakupowej
  const { mutate: exportShoppingList, isLoading: isExporting } =
    usePostShoppingListExport(shoppingListId, {
      onSuccess: (data) => {
        const a = document.createElement("a");
        a.download = data.file_name;
        a.href = `data:application/octet-stream;base64,${data.content}`;
        a.click();
      },
    });

  // aktualizacja pozycji
  const { mutate: updatePosition } = usePutShoppingListPosition(
    shoppingListId,
    {
      onSuccess: () => {
        refetchShoppingListPositions();
      },
      onSettled: (data, error, params) => {
        setUpdatingPositionIds((prevState) =>
          prevState.filter((item) => item !== params.positionId)
        );
      },
    }
  );

  // zwiększenie ilości
  const { mutate: increaseQuantity } =
    usePostShoppingListPositionQuantityIncrement(shoppingListId, {
      onSuccess: () => {
        refetchShoppingListPositions();
      },
      onSettled: (data, error, params) => {
        setUpdatingPositionIds((prevState) =>
          prevState.filter((item) => item !== params.positionId)
        );
      },
    });

  // zmniejszenie ilości
  const { mutate: decreaseQuantity } =
    usePostShoppingListPositionQuantityDecrement(shoppingListId, {
      onSuccess: () => {
        refetchShoppingListPositions();
      },
      onSettled: (data, error, params) => {
        setUpdatingPositionIds((prevState) =>
          prevState.filter((item) => item !== params.positionId)
        );
      },
    });

  const handleChangeQuantity = (
    position: IShoppingListPositionListItem,
    newValue: number | null,
    isIncrement?: boolean
  ) => {
    setUpdatingPositionIds((prevState) => [...prevState, position.id]);

    newValue === null
      ? isIncrement
        ? increaseQuantity({
            positionId: position.id,
          })
        : decreaseQuantity({
            positionId: position.id,
          })
      : updatePosition({
          positionId: position.id,
          quantity: newValue,
          unit_id: position.unit_id,
        });
  };

  const handleChangeUnit = (
    position: IShoppingListPositionListItem,
    unitId: number
  ) => {
    setUpdatingPositionIds((prevState) => [...prevState, position.id]);

    updatePosition({
      positionId: position.id,
      quantity: position.quantity,
      unit_id: unitId,
    });
  };

  const columns: IColumn<IShoppingListPositionListItem>[] = useMemo(
    () => [
      {
        title: (
          <MuiCheckbox
            checked={
              checkedItemIds.length > 0 &&
              checkedItemIds.length === shoppingListPositions?.items.length
            }
            indeterminate={
              checkedItemIds.length > 0 &&
              checkedItemIds.length <
                (shoppingListPositions?.items || []).length
            }
            onClick={(event) => {
              event.stopPropagation();
              setCheckedItemIds((old) =>
                old.length < (shoppingListPositions?.items || []).length
                  ? (shoppingListPositions?.items || []).map(
                      (position) => position.id
                    )
                  : []
              );
            }}
          />
        ),
        key: "lp",
        align: "left",
        isMobileHidden: true,
        renderCell: (item, index) => {
          const isChecked = checkedItemIds.includes(item.id);

          return (
            <div className={styles.checkboxWrapper}>
              <Checkbox
                checked={isChecked}
                onClick={() => {
                  setCheckedItemIds((old) =>
                    isChecked
                      ? old.filter((checkbox) => checkbox !== item.id)
                      : [...old, item.id]
                  );
                }}
              />
              <div className={styles.lp}>{index + 1}</div>
            </div>
          );
        },
        width: 86,
      },
      {
        title: "",
        key: "product-image",
        renderCell: (item) => {
          const images = (item as any).images ?? item.image ?? [];
          const displayableExtensions = [".png", ".jpg", ".jpeg", ".webp", ".gif"];
          const displayableImage =
            images.find((img: { thumb?: string }) =>
              displayableExtensions.some((ext) =>
                (img.thumb || "").toLowerCase().endsWith(ext)
              )
            ) ?? images[0];
          const thumbUrl = displayableImage?.thumb;

          return (
            <div className={styles.productThumb}>
              <Link to={`/${item?.url_link}`}>
                {thumbUrl ? (
                  <img src={thumbUrl} alt={item.name} />
                ) : (
                  <div className={styles.productThumbPlaceholder} />
                )}
              </Link>
            </div>
          );
        },
        width: 78,
      },
      {
        title: <Trans>Nazwa produktu</Trans>,
        key: "name",
        align: "left",
        renderCell: (item) => {
          return (
            <div className={styles.title}>
              <Link to={`/${item?.url_link}`} className={styles.name}>
                {item.name}
              </Link>
              <small>{item.index}</small>
            </div>
          );
        },
      },
      {
        title: t("Cena"),
        key: "price",
        align: "right",
        renderCell: (item) => (
          <div className={styles.productPriceCombined}>
            <div
              className={classnames({
                [styles.priceBlue]: hasSpecialPriceLabel(item),
              })}
            >
              {item?.price_net_formatted} {item.currency}{" "}
              <span className={styles.suffix}><Trans>netto</Trans></span>
            </div>
            <div
              className={classnames(styles.small, {
                [styles.priceBlue]: hasSpecialPriceLabel(item),
              })}
            >
              {item?.price_gross_formatted} {item.currency}{" "}
              <span className={styles.suffix}><Trans>brutto</Trans></span>
            </div>
          </div>
        ),
      },
      {
        title: `${t("Wartość")}`,
        dataIndex: "total_price_net",
        align: "right",
        renderCell: (item) => (
          <div className={styles.productTotalPrice}>
            <div
              className={classnames({
                [styles.priceBlue]: hasSpecialPriceLabel(item),
              })}
            >
              {item.total_price_gross_formatted} {item.currency}{" "}
              <span className={styles.suffix}>
                <Trans>brutto</Trans>
              </span>
            </div>
            <div
              className={classnames(styles.small, {
                [styles.priceBlue]: hasSpecialPriceLabel(item),
              })}
            >
              {item.total_price_net_formatted} {item.currency}{" "}
              <span className={styles.suffix}>
                <Trans>netto</Trans>
              </span>
            </div>
          </div>
        ),
      },
      {
        title: <Trans>Do koszyka</Trans>,
        key: "addToCart",
        align: "center" as const,
        renderCell: (item: IShoppingListPositionListItem) => (
          <AddToCartCell
            item={item}
            disabled={updatingPositionIds.some(
              (updatingPositionId) => item.id === updatingPositionId
            )}
          />
        ),
      },
      ...(!isBlocked
        ? [
            {
              title: <Trans>Usuń</Trans>,
              key: "delete",
              align: "center" as const,
              renderCell: (item: IShoppingListPositionListItem) => (
                <TrashIcon
                  className={styles.deleteButton}
                  onClick={() => setItemToDelete(item)}
                />
              ),
            },
          ]
        : []),
    ],
    [checkedItemIds, setCheckedItemIds, updatingPositionIds, isBlocked]
  );

  return (
    <>
      <div
        className={classnames(
          styles.componentWrapper,
          "StylePath-Pages-ShoppingLists-components-ShoppingListPositions"
        )}
      >
        <div className={styles.toolbar}>
          <div className={styles.actionWrapper}>
            <ArrowDropdown className={styles.arrow} />
            <div className={styles.actionSelectWrapper}>
              <AddPositionsToCartSelect
                positions={
                  shoppingListPositions?.items
                    .filter((item) => checkedItemIds.includes(item.id))
                    .map((item) => ({
                      product_id: item.product_id,
                      quantity: item.quantity,
                      unit_id: item.unit_id,
                    })) || []
                }
                onSuccess={() => setCheckedItemIds([])}
              />
            </div>

            <button
              className={styles.exportButton}
              onClick={() => exportShoppingList({ export_type: exportFileType })}
              disabled={isExporting}
            >
              <FileExportIcon />
              <Trans>Exportuj</Trans>
            </button>

            <WiseSelect<{ file_type: IExportFileType }>
              onChange={(item) => {
                // Type guard - sprawdzamy czy to nie jest tablica (multichoice)
                if (!Array.isArray(item) && item) {
                  setExportFileType(item?.file_type);
                }
              }}
              options={[
                {
                  value: "XLS",
                  label: "XLS",
                  item: { file_type: "XLS" },
                },
                {
                  value: "CSV",
                  label: "CSV",
                  item: { file_type: "CSV" },
                },
              ]}
              value={exportFileType}
            />
          </div>

          <div className={styles.searchWrapper}>
            <SearchInput
              value={searchQuery}
              placeholder={`${t("Szukaj produktu w liście")}...`}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <Table<IShoppingListPositionListItem>
          columns={columns}
          dataSource={shoppingListPositions?.items || []}
          rowKey="id"
          pagination={
            (shoppingListPositions?.total_pages || 1) > 1
              ? {
                  page: queryParams.page || 1,
                  pagesCount: shoppingListPositions?.total_pages || 1,
                  onChange: (page) =>
                    setQueryParams((prevState) => ({ ...prevState, page }))
                }
              : undefined
          }
        />
      </div>

      {itemToDelete && (
        <Modal
          title={t("Potwierdzenie usunięcia")}
          onClose={() => setItemToDelete(null)}
        >
          <div className={styles.confirmationModal}>
            <Trans>Czy na pewno chcesz usunąć produkt?</Trans>
            <div className={styles.confirmationModalActions}>
              <Button
                onClick={() => setItemToDelete(null)}
                color="danger"
                ghost
              >
                <Trans>Anuluj</Trans>
              </Button>

              <Button
                color="danger"
                loading={isRemoving}
                onClick={() => deleteShoppingListPosition()}
              >
                <Trans>Usuń</Trans>
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ShoppingListPositions;
